import { useChatStore } from '@/store/chat'
import type { HtmlPublishKey } from '@/utils/publishing'

/**
 * Keeps a chat message's `html_block` file in step with a website that was just
 * unpublished or deleted, so the asset card and the preview toolbar re-render
 * from `file.metadata` (EVGPT-1532) without waiting for a chat refetch.
 *
 * Shared by both surfaces — they must apply the same mutation or the card and
 * the preview disagree about publish state.
 */
export function useHtmlBlockPublishSync() {
  const chatStore = useChatStore()

  /**
   * Keep the file and `websiteId`, flipping only `is_published`, so the block
   * reads "Unpublished". A legacy per-block publish has no website to point at,
   * so there the file itself is dropped.
   */
  const syncBlockUnpublished = (
    key: HtmlPublishKey,
    websiteId?: string | null,
  ) => {
    if (websiteId) {
      chatStore.setAssistantHtmlBlockWebsite(
        key.chatId,
        key.messageId,
        key.blockIndex,
        { websiteId, isPublished: false },
      )
      return
    }
    chatStore.markAssistantHtmlBlockUnpublished(
      key.chatId,
      key.messageId,
      key.blockIndex,
    )
  }

  /**
   * The website and its published page are gone, so drop the block's published
   * file and let it fall back to "Draft" — on the next chat load the backend
   * excludes the soft-deleted website and `file.metadata` is null anyway.
   */
  const syncBlockDeleted = (key: HtmlPublishKey) => {
    chatStore.markAssistantHtmlBlockUnpublished(
      key.chatId,
      key.messageId,
      key.blockIndex,
    )
  }

  return { syncBlockUnpublished, syncBlockDeleted }
}
